import Loader from 'Common/Loader';
import React, { useState, useEffect } from 'react';
import { Modal, Row, Col } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { useForm } from 'react-hook-form';
import DataGrid, { Column, Paging, Scrolling } from 'devextreme-react/cjs/data-grid';
import { PaymentEntry, PaymentFormModel } from '../models/payment-form-model';

interface PaymentModalProps {
    show: boolean;
    onHide: () => void;
    reloadDataSource?: () => void;
    handelPaymentData?: (entries: PaymentEntry[]) => void;
    initialPaymentData?: PaymentEntry[];
    maxAllowedAmount?: number;
}

const PaymentModal: React.FC<PaymentModalProps> = ({
    show,
    onHide,
    reloadDataSource,
    handelPaymentData,
    initialPaymentData = [],
    maxAllowedAmount = 0,
}) => {
    const [loading, setLoading] = useState(false);
    const [paymentEntries, setPaymentEntries] = useState<PaymentEntry[]>([]);
    const [editingEntry, setEditingEntry] = useState<PaymentEntry | null>(null);
    const [isEditMode, setIsEditMode] = useState(false);

    const { register, handleSubmit, reset, setValue, formState: { errors } } = useForm<PaymentFormModel>({
        defaultValues: {
            paymentType: '',
            referenceNumber: '',
            note: ''
        },
        mode: 'onChange'
    });


    useEffect(() => {
        if (!isEditMode) {
            reset({
                paymentType: '',
                referenceNumber: '',
                note: ''
            });
            setEditingEntry(null);
        }
    }, [isEditMode, reset]);

    useEffect(() => {
        if (handelPaymentData && show) {
            handelPaymentData(paymentEntries);
        }
    }, [paymentEntries, handelPaymentData, show]);

    const onAddEntry = (data: PaymentFormModel) => {
        if (!data.paymentType || data.amount <= 0) {
            toast.error('Please fill all required fields');
            return;
        }
        const currentTotal = paymentEntries.reduce(
            (sum, entry) => sum + parseFloat(entry.amount.toString().replace(/,/g, '')),
            0
        );

        let newTotal = currentTotal;
        if (isEditMode && editingEntry) {
            newTotal = currentTotal - editingEntry.amount + data.amount;
        } else {
            newTotal = currentTotal + data.amount;
        }

        //  Don't allow overpayment
        if (maxAllowedAmount > 0 && newTotal > maxAllowedAmount) {
            toast.error(`Total payment cannot exceed ₹${maxAllowedAmount}`);
            return;
        }

        if (isEditMode && editingEntry) {
            const updatedEntry: PaymentEntry = {
                ...editingEntry,
                type: data.paymentType,
                amount: data.amount,
                referenceNumber: data.referenceNumber,
                note: data.note
            };

            setPaymentEntries(prev => {
                const updatedEntries = prev.map(entry => entry.id === editingEntry.id ? updatedEntry : entry);
                return updatedEntries;
            });

            toast.success('Payment entry updated successfully!');
            setIsEditMode(false);
            setEditingEntry(null);
        } else {
            const newEntry: PaymentEntry = {
                id: Date.now().toString(),
                type: data.paymentType,
                amount: data.amount,
                referenceNumber: data.referenceNumber,
                note: data.note
            };

            setPaymentEntries(prev => [...prev, newEntry]);
        }

        reset({
            paymentType: '',
            amount: 0,
            referenceNumber: '',
            note: ''
        });
    };

    const editEntry = (entry: PaymentEntry) => {
        setEditingEntry(entry);
        setIsEditMode(true);

        setValue('paymentType', entry.type);
        setValue('amount', entry.amount);
        setValue('referenceNumber', entry.referenceNumber || '');
        setValue('note', entry.note || '');

        toast.info('Entry loaded for editing');
    };

    const cancelEdit = () => {
        setIsEditMode(false);
        setEditingEntry(null);
        reset({
            paymentType: '',
            amount: 0,
            referenceNumber: '',
            note: ''
        });
    };

    const handleSave = async () => {
        if (paymentEntries.length === 0) {
            toast.error('Please add at least one payment entry');
            return;
        }

        try {
            setLoading(true);

            await new Promise(resolve => setTimeout(resolve, 1000));

            toast.success('Payment details saved successfully!');

            if (handelPaymentData) {
                handelPaymentData(paymentEntries);
            }

            if (reloadDataSource) {
                reloadDataSource();
            }

            handleClose();
        } catch (error: any) {
            toast.error(error.message || 'Error saving payment details');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setIsEditMode(false);
        setEditingEntry(null);
        reset();
        onHide();
    };

    const removeEntry = (id: string) => {
        setPaymentEntries(prev => {
            const updatedEntries = prev.filter(entry => entry.id !== id);
            return updatedEntries;
        });

        if (editingEntry && editingEntry.id === id) {
            cancelEdit();
        }
    };

    const renderHeaderCell = (headerInfo: any) => {
        return <div className="text-nowrap">{headerInfo.column.caption}</div>;
    };

    const renderActionCell = (cellData: any) => {
        const isCurrentlyEditing = editingEntry && editingEntry.id === cellData.data.id;

        return (
            <div className="d-flex gap-1">
                {/* <button
                    type="button"
                    className={`btn btn-sm ${isCurrentlyEditing ? 'btn-warning' : 'btn-info'}`}
                    onClick={() => editEntry(cellData.data)}
                    title="Edit Entry"
                    disabled={isEditMode && !isCurrentlyEditing}
                >
                    <i className="bx bx-edit"></i>
                </button> */}
                <button
                    type="button"
                    className="btn btn-danger btn-sm"
                    onClick={() => removeEntry(cellData.data.id)}
                    title="Remove Entry"
                >
                    <i className="bx bx-trash"></i>
                </button>
            </div>
        );
    };

    const renderCurrencyCell = (cellData: any) => {
        return `₹${cellData.value}`;
    };

    const totalAmount = paymentEntries.reduce((sum, entry) => sum + parseFloat(entry.amount.toString().replace(/,/g, '')), 0);

    const formattedTotal = totalAmount.toFixed(2);
    return (
        <>
            <Modal
                show={show}
                size='xl'
                onHide={handleClose}
                className="fade"
                dialogClassName="modal-dialog-centered"
                backdrop="static"
            >
                <Modal.Header closeButton className="border-0">
                    <div className="d-flex align-items-center">
                        <i className="bx bx-credit-card  me-2"></i>
                        <span className="fw-bold ">Payment Details</span>
                        {initialPaymentData.length > 0 && (
                            <span className="badge bg-info ms-2">Editing Mode</span>
                        )}
                    </div>
                </Modal.Header>
                <Modal.Body>
                    <Row>
                        <Col lg={12}>
                            {/* Payment Entry Form */}
                            <div className="card mb-4">
                                <div className="card-header d-flex justify-content-between align-items-center">
                                    <h6 className="mb-0">
                                        {isEditMode ? 'Edit Payment Entry' : 'Add Payment Entry'}
                                    </h6>
                                    {isEditMode && (
                                        <button
                                            type="button"
                                            className="btn btn-outline-secondary btn-sm"
                                            onClick={cancelEdit}
                                        >
                                            Cancel Edit
                                        </button>
                                    )}
                                </div>
                                <div className="card-body">
                                    <form onSubmit={handleSubmit(onAddEntry)}>
                                        <div className="row">
                                            <div className="col-md-3 mb-3">
                                                <label className="form-label text-muted small">Payment Type <span className="text-danger">*</span></label>
                                                <select
                                                    className={`form-control form-control-sm ${errors.paymentType ? "is-invalid" : ""}`}
                                                    {...register("paymentType", { required: "Payment type is required" })}
                                                >
                                                    <option value="">Select Payment Type</option>
                                                    <option value="Cash">Cash</option>
                                                    <option value="Card">Card</option>
                                                    <option value="UPI">UPI</option>
                                                    <option value="Net Banking">Net Banking</option>
                                                    <option value="Cheque">Cheque</option>
                                                    {/* <option value="Digital Wallet">Digital Wallet</option> */}
                                                </select>
                                                {errors.paymentType && <span className="text-danger small">{String(errors.paymentType.message)}</span>}
                                            </div>

                                            <div className="col-md-3 mb-3">
                                                <label className="form-label text-muted small">Amount <span className="text-danger">*</span></label>
                                                <input
                                                    className={`form-control form-control-sm ${errors.amount ? "is-invalid" : ""}`}
                                                    type="text"
                                                    inputMode="decimal" 
                                                    placeholder="0.00"
                                                    {...register("amount", {
                                                        required: "Amount is required",
                                                        min: { value: 0.01, message: "Amount must be greater than 0" },
                                                        validate: (value) =>
                                                            /^\d{1,9}(\.\d{1,2})?$/.test(String(value)) || "Up to 9 digits before decimal and 2 decimals allowed",
                                                    })}
                                                    onKeyDown={(e) => {
                                                        if (e.ctrlKey || e.metaKey) {
                                                            return;
                                                        }
                                                        if (
                                                            !/[0-9.]/.test(e.key) &&
                                                            !["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab"].includes(e.key)
                                                        ) {
                                                            e.preventDefault();
                                                        }

                                                        if (e.key === "." && (e.currentTarget.value.includes(".") || e.currentTarget.value === "")) {
                                                            e.preventDefault();
                                                        }
                                                    }}
                                                    onInput={(e) => {
                                                        const val = e.currentTarget.value;
                                                        const match = val.match(/^\d{0,9}(\.\d{0,2})?/);
                                                        e.currentTarget.value = match ? match[0] : "";
                                                    }}
                                                />
                                                {errors.amount && <span className="text-danger small">{String(errors.amount.message)}</span>}
                                            </div>

                                            <div className="col-md-3 mb-3">
                                                <label className="form-label text-muted small">Reference Number</label>
                                                <input
                                                    className="form-control form-control-sm"
                                                    placeholder="Reference Number"
                                                    {...register("referenceNumber")}
                                                    maxLength={25}
                                                />
                                            </div>

                                            {/* <div className="col-md-3 mb-3">
                                                <label className="form-label text-muted small">Note</label>
                                                <input
                                                    className={`form-control form-control-sm ${errors.note ? "is-invalid" : ""}`}
                                                    placeholder="Note"
                                                    maxLength={150}
                                                    {...register("note", {
                                                        maxLength: {
                                                            value: 150,
                                                            message: "Note must be 150 characters or less",
                                                        },  
                                                    })}
                                                />
                                                {errors.note && (
                                                    <div className="invalid-feedback">{errors.note.message}</div>
                                                )}
                                            </div> */}

                                        </div>

                                        <div className="d-flex justify-content-end">
                                            <button
                                                type="submit"
                                                className={`btn btn-sm px-4 ${isEditMode ? 'btn-warning' : 'btn-primary'}`}
                                            >
                                                <i className={`bx ${isEditMode ? 'bx-save' : 'bx-plus'} me-1`}></i>
                                                {isEditMode ? 'Update Payment' : 'Add Payment'}
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            </div>

                            {paymentEntries.length > 0 && (
                                <div className="mb-4">
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <h6 className="fw-bold mb-0">Payment Entries</h6>
                                        <div className="text-muted small">
                                            Total Amount: <span className="fw-bold">₹ {' '}{formattedTotal}</span>
                                        </div>
                                    </div>

                                    <DataGrid
                                        dataSource={paymentEntries}
                                        keyExpr="id"
                                        showBorders={true}
                                        showRowLines={true}
                                        allowColumnReordering={true}
                                        allowColumnResizing={true}
                                        columnAutoWidth={true}
                                        wordWrapEnabled={true}
                                    >
                                        <Paging defaultPageSize={10} />
                                        <Scrolling mode="standard" />
                                        <Column
                                            caption="Action"
                                            width={120}
                                            cellRender={renderActionCell}
                                            headerCellRender={renderHeaderCell}
                                            allowSorting={false}
                                            allowFiltering={false}
                                        />
                                        <Column
                                            dataField="type"
                                            caption="Type"
                                            width={200}
                                            dataType="string"
                                            headerCellRender={renderHeaderCell}
                                        />
                                        <Column
                                            dataField="referenceNumber"
                                            caption="Reference Number"
                                            width={450}
                                            dataType="string"
                                            headerCellRender={renderHeaderCell}
                                        />
                                        <Column
                                            dataField="amount"
                                            caption="Amount"

                                            format="#,##0.00"
                                            dataType="number"
                                        // cellRender={renderCurrencyCell}
                                        // headerCellRender={renderHeaderCell}
                                        />
                                        {/* <Column
                                            dataField="note"
                                            caption="Note"
                                            dataType="string"
                                            headerCellRender={renderHeaderCell}
                                            width={150}
                                            cellRender={({ data }) => (
                                                <div
                                                    title={data.note}
                                                    style={{
                                                        overflow: "hidden",
                                                        textOverflow: "ellipsis",
                                                        whiteSpace: "nowrap",
                                                        maxWidth: "100%",
                                                    }}
                                                >
                                                    {data.note}
                                                </div>
                                            )}
                                        /> */}

                                    </DataGrid>
                                </div>
                            )}
                        </Col>
                    </Row>
                </Modal.Body>
                <Modal.Footer className="border-0">
                    <div className="d-flex justify-content-end gap-2">
                        <button
                            type="button"
                            className="btn add-btn btn-sm px-4"
                            onClick={handleClose}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            className="btn submit-btn btn-sm px-4"
                            onClick={handleSave}
                            disabled={loading || paymentEntries.length === 0}
                        >
                            {loading ? 'Saving...' : 'Save Payment'}
                        </button>
                    </div>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default PaymentModal;